import {Button, Group, Paper, Select, Stack, Text, Title} from '@mantine/core'
import {useState} from 'react'

import {useCurrentUser} from 'src/auth/use-current-user'
import {isManager} from 'src/jobs/edit-rights'

import {usePaymentTermsOptions} from './payment-terms'
import type {CustomerDetail} from './schemas'
import {useUpdateCustomerPaymentTerms} from './use-customers'

type CustomerPaymentTermsSectionProps = {
    customer: Pick<CustomerDetail, 'id' | 'paymentTerms'>
}

export function CustomerPaymentTermsSection({customer}: CustomerPaymentTermsSectionProps) {
    const currentUser = useCurrentUser()
    const canChange = currentUser != null && isManager(currentUser)
    const [draft, setDraft] = useState<string | null>(null)
    const mutation = useUpdateCustomerPaymentTerms(customer.id)
    const termsOptions = usePaymentTermsOptions(customer.paymentTerms)

    function handleSave() {
        if (draft == null) {
            return
        }

        mutation.mutate(draft, {onSuccess: () => setDraft(null)})
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="sm">
                <Group justify="space-between" align="center">
                    <Title order={3}>Payment terms</Title>
                    {canChange && draft == null ? (
                        <Button
                            variant="light"
                            size="xs"
                            onClick={() => setDraft(customer.paymentTerms ?? '')}
                        >
                            Edit payment terms
                        </Button>
                    ) : null}
                </Group>
                {draft == null ? (
                    <Text size="sm" c={customer.paymentTerms ? undefined : 'dimmed'}>
                        {customer.paymentTerms ?? 'Not set'}
                    </Text>
                ) : (
                    <Group align="flex-end" gap="sm">
                        <Select
                            label="Payment terms"
                            placeholder="Select payment terms"
                            data={termsOptions}
                            value={draft || null}
                            onChange={(value) => setDraft(value ?? '')}
                            error={mutation.error instanceof Error ? mutation.error.message : null}
                            allowDeselect={false}
                        />
                        <Button loading={mutation.isPending} onClick={handleSave}>
                            Save
                        </Button>
                        <Button variant="subtle" onClick={() => setDraft(null)}>
                            Cancel
                        </Button>
                    </Group>
                )}
                <Text size="xs" c="dimmed">
                    {canChange
                        ? 'Default terms for new quotes'
                        : 'Default terms for new quotes (admins and managers only)'}
                </Text>
            </Stack>
        </Paper>
    )
}
